import {
  CareProgramsAPIResponse,
  HealthTopicAPIResponse,
  ContentSearchAPIResponse,
  ContentSearchParams,
  ContentSearchQueryData,
  LogEngagementParams,
  BenefitsAPIResponse,
  BenefitsParams,
  BenefitsQueryData,
} from 'features/resources/types';
import { LibraryContentAPIResponse } from 'features/resource/types';
import { WellframeApi } from 'store/api';
import {
  LIBRARY_PAGE_SIZE,
  hasMoreResults,
  mergeContentSearchResults,
  normalizeContentSearchResponse,
} from 'features/resources/utils/contentSearchUtils';
import {
  BENEFITS_PAGE_SIZE,
  mergeBenefitsPages,
  normalizeBenefitsResponse,
} from 'features/resources/utils/benefitsUtils';

export const resourceService = WellframeApi.injectEndpoints({
  endpoints: (builder) => ({
    getLibraryArticles: builder.query<LibraryContentAPIResponse[], { page: number }>({
      query: ({ page }) => ({
        url: '/api/v3/contents',
        params: { page, page_size: LIBRARY_PAGE_SIZE },
      }),
      serializeQueryArgs: ({ endpointName }) => endpointName,
      merge: (currentCache, newItems, { arg }) =>
        arg.page === 0 ? newItems : [...currentCache, ...newItems],
      forceRefetch: ({ currentArg, previousArg }) => currentArg?.page !== previousArg?.page,
    }),
    getHealthTopics: builder.query<HealthTopicAPIResponse[], { memberUuid: string }>({
      query: ({ memberUuid }) =>
        `/mobileapi/v1/members/${memberUuid}/care-programs/-/health-topics`,
    }),
    getBenefits: builder.query<BenefitsQueryData, BenefitsParams>({
      query: ({ memberUuid, page = 1, pageSize = BENEFITS_PAGE_SIZE, language }) => ({
        url: `/mobileapi/v1/members/${memberUuid}/benefits`,
        params: { page, pageSize },
        headers: { 'Accept-Language': language },
      }),
      transformResponse: (response: BenefitsAPIResponse, _meta, arg): BenefitsQueryData =>
        normalizeBenefitsResponse(response, arg.page ?? 1),
      // One cache entry per member/page size; pages accumulate inside it. A language
      // change refetches page 1, which replaces the entry (see mergeBenefitsPages).
      serializeQueryArgs: ({ queryArgs: { memberUuid, pageSize } }) => `${memberUuid}-${pageSize}`,
      merge: (currentCache, newData, { arg }) =>
        mergeBenefitsPages(currentCache, newData, arg.page ?? 1),
      // Cache key ignores page and language; force refetch so changes to either still fetch.
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.page !== previousArg?.page || currentArg?.language !== previousArg?.language,
    }),
    logEngagement: builder.mutation<void, LogEngagementParams>({
      query: ({ memberUuid, body }) => ({
        url: `/mobileapi/v1/members/${memberUuid}/engagement-logs`,
        method: 'POST',
        body,
      }),
    }),
    searchContent: builder.query<ContentSearchQueryData, ContentSearchParams>({
      query: ({ search, page = 0, pageSize = 30 }) => ({
        url: '/api/v3/contents',
        params: { search, page, page_size: pageSize },
      }),
      transformResponse: (response: Partial<ContentSearchAPIResponse>): ContentSearchQueryData => {
        const normalized = normalizeContentSearchResponse(response);
        return {
          ...normalized,
          careProgramsHasMore: hasMoreResults(normalized.care_programs),
          onDemandHasMore: hasMoreResults(normalized.on_demand),
        };
      },
      // One cache entry per search term.
      serializeQueryArgs: ({ queryArgs }) => queryArgs.search,
      merge: (currentCache, newItems, { arg }) =>
        mergeContentSearchResults(currentCache, newItems, arg.page ?? 0),
      // Cache key ignores page; force refetch so page changes still fetch.
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.page !== previousArg?.page || currentArg?.search !== previousArg?.search,
    }),
  }),
});

export const {
  useGetLibraryArticlesQuery,
  useGetHealthTopicsQuery,
  useSearchContentQuery,
  useLogEngagementMutation,
  useGetBenefitsQuery,
} = resourceService;
